import { GaugeArc } from "./GaugeArc";
import { GaugeNeedle } from "./GaugeNeedle";
import type { GaugeSegment, GaugeConfig } from "../types";
import { valueToAngle, getLabelPosition, mapRange } from "../utils/arc-math";

export const Gauge = ({
  value,
  segments,
  labels,
  config,
  className,
  showNeedle = true
}: {
  value: number;
  segments: GaugeSegment[];
  labels: number[];
  config: GaugeConfig;
  className?: string;
  showNeedle?: boolean;
}) => {
  const { viewBox, center, radius, minValue, maxValue } = config;

  const clampedValue = Math.max(minValue, Math.min(maxValue, value));
  // Needle rotation in CSS: 0° = pointing up, positive = clockwise
  // Score 300 -> -90° (pointing left)
  // Score 850 -> +90° (pointing right)
  const needleRotation = mapRange(clampedValue, minValue, maxValue, -90, 90);

  return (
    <svg
      viewBox={`0 0 ${viewBox.width} ${viewBox.height}`}
      className={className}
      role="img"
      aria-label={`Gauge showing value ${value}`}
    >
      <GaugeArc segments={segments} config={config} />

      {labels.map((labelValue) => {
        const angle = valueToAngle(labelValue, minValue, maxValue);
        const pos = getLabelPosition(center.x, center.y, radius, angle);
        // Rotate label to follow the arc curve
        // angle 180 (left) -> rotate -90deg, angle 0 (right) -> rotate +90deg
        const labelRotation = 90 - angle;
        return (
          <text
            key={labelValue}
            x={pos.x}
            y={pos.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#8b8d97"
            fontSize="10"
            fontWeight="500"
            transform={`rotate(${labelRotation}, ${pos.x}, ${pos.y})`}
          >
            {labelValue}
          </text>
        );
      })}

      {showNeedle ? (
        <GaugeNeedle rotation={needleRotation} config={config} />
      ) : null}
    </svg>
  );
};
